import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, EnvelopeSimple, WarningCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import AuthActionShell, { authButtonClass, authInputClass } from "@/components/AuthActionShell";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [email, setEmail] = useState(params.get("email") || "");
  const [status, setStatus] = useState(token ? "verifying" : "waiting");
  const [message, setMessage] = useState(token ? "Verifying your email..." : "Check your inbox and open the verification link.");
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    api.post("/auth/verify-email", { token })
      .then(({ data }) => {
        setStatus("success");
        setMessage(data.message);
      })
      .catch((error) => {
        setStatus("error");
        const detail = error?.response?.data?.detail;
        setMessage(typeof detail === "string" ? detail : "This verification link could not be used.");
      });
  }, [token]);

  const resend = async (event) => {
    event.preventDefault();
    setStatus("sending");
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      setStatus("waiting");
      setMessage(data.message);
    } catch {
      setStatus("waiting");
      setMessage("If that account still needs verification, a new link has been sent.");
    }
  };

  const statusClass = status === "success"
    ? "is-success"
    : status === "error"
      ? "is-error"
      : "is-neutral";

  return (
    <AuthActionShell
      eyebrow="Email verification"
      title="Verify your email"
      description="Verification protects your private-beta account and is required before the first sign-in."
    >
      <div className={`auth-action-status ${statusClass}`} role="status" aria-live="polite">
        {status === "success"
          ? <CheckCircle size={19} weight="fill" />
          : status === "error"
            ? <WarningCircle size={19} weight="fill" />
            : <EnvelopeSimple size={19} weight="duotone" />}
        <span>{message}</span>
      </div>

      {status !== "success" && status !== "verifying" && (
        <form onSubmit={resend} className="auth-action-form auth-action-form--spaced">
          <label className="auth-action-label">
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="email"
              className={authInputClass}
            />
          </label>
          <button type="submit" disabled={status === "sending"} className={authButtonClass}>
            {status === "sending" ? "Sending..." : "Send a new verification link"}
          </button>
        </form>
      )}

      <Link to="/login" className="auth-action-text-link">Continue to sign in →</Link>
    </AuthActionShell>
  );
}
